import { useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Wine, ShoppingBasket } from "lucide-react";
import { Helmet } from "react-helmet";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Seo } from "@/components/common";
import { useGardforsaljningProducers } from "@/hooks/useGardforsaljningProducers";
import {
  GARDFORSALJNING_REGIONS,
  findRegionBySlug,
} from "@/data/gardforsaljningRegions";
import {
  buildHubTitle,
  buildHubDescription,
  buildRegionTitle,
  buildRegionDescription,
  buildItemListJsonLd,
  buildFaqJsonLd,
  GARDFORSALJNING_FAQ,
} from "@/lib/seo/gardforsaljningSeo";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE_URL = "https://farmly.se";

/**
 * Shared page for /gardforsaljning and /gardforsaljning/:regionSlug
 *
 * Hub mode (/gardforsaljning):
 *  - Fetches ALL producers with has_farm_sale_alcohol
 *  - Derives dynamic region chips from data (shown only when ≥2 regions)
 *  - Supports ?region=<slug> for in-page filtering
 *
 * Region mode (/gardforsaljning/:regionSlug):
 *  - Filters server-side by region for SEO sub-pages
 */
const GardforsaljningPage = () => {
  const { regionSlug } = useParams<{ regionSlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const region = regionSlug ? findRegionBySlug(regionSlug) : undefined;
  const isHub = !regionSlug;
  const is404Region = regionSlug && !region;

  // Hub: fetch all; Region sub-page: fetch filtered
  const { data: allProducers, isLoading } = useGardforsaljningProducers(
    isHub ? undefined : region
  );

  // ── Dynamic regions (hub only) ──
  const dynamicRegions = useMemo(() => {
    if (!isHub || !allProducers) return [];
    const regionSet = new Map<string, number>();
    for (const p of allProducers) {
      const r = p.region;
      if (r) regionSet.set(r, (regionSet.get(r) || 0) + 1);
    }
    return Array.from(regionSet.entries())
      .map(([name, count]) => ({ name, count, slug: toSlug(name) }))
      .sort((a, b) => a.name.localeCompare(b.name, "sv"));
  }, [isHub, allProducers]);

  const showRegionChips = dynamicRegions.length >= 2;

  // ── In-page region filter (hub only) ──
  const activeRegionSlug = isHub ? searchParams.get("region") || null : null;

  const filteredProducers = useMemo(() => {
    if (!allProducers) return [];
    if (!isHub || !activeRegionSlug) return allProducers;
    const activeRegion = dynamicRegions.find((r) => r.slug === activeRegionSlug);
    if (!activeRegion) return allProducers;
    return allProducers.filter((p) => p.region === activeRegion.name);
  }, [allProducers, isHub, activeRegionSlug, dynamicRegions]);

  // ── SEO ──
  const title = isHub ? buildHubTitle() : buildRegionTitle(region!.label);
  const description = isHub
    ? buildHubDescription()
    : buildRegionDescription(region!.label);
  const canonicalPath = isHub
    ? "/gardforsaljning"
    : `/gardforsaljning/${region!.slug}`;
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;

  const h1 = isHub
    ? "Gårdsförsäljning i Sverige"
    : `Gårdsförsäljning i ${region!.label}`;

  const jsonLd = useMemo(() => {
    if (!filteredProducers || filteredProducers.length === 0) return undefined;
    const pageName = isHub
      ? "Gårdsförsäljning i Sverige"
      : `Gårdsförsäljning i ${region!.label}`;
    const itemList = buildItemListJsonLd(filteredProducers, canonicalUrl, pageName);
    if (isHub) {
      const faq = buildFaqJsonLd(GARDFORSALJNING_FAQ);
      return [itemList, faq];
    }
    return itemList;
  }, [filteredProducers, isHub, region, canonicalUrl]);

  // ── Region chip click handler ──
  const handleRegionSelect = (slug: string | null) => {
    if (slug) {
      setSearchParams({ region: slug });
    } else {
      setSearchParams({});
    }
  };

  // ── 404 region ──
  if (is404Region) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Helmet>
          <title>Sidan hittades inte – Farmly</title>
        </Helmet>
        <SiteHeader density="compact" />
        <main className="flex-1 container py-16 text-center">
          <h1 className="font-display text-2xl mb-4 text-foreground">
            Regionen hittades inte
          </h1>
          <p className="text-muted-foreground mb-6">
            Vi kunde inte hitta gårdsförsäljning för den regionen.
          </p>
          <Link
            to="/gardforsaljning"
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Alla regioner
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        jsonLd={jsonLd}
      />
      <SiteHeader density="compact" />

      <main className="flex-1 container py-10 sm:py-14">
        {/* Breadcrumb */}
        <nav className="mb-6 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">
            Farmly
          </Link>
          <span className="mx-1.5">›</span>
          {region ? (
            <>
              <Link
                to="/gardforsaljning"
                className="hover:text-foreground transition-colors"
              >
                Gårdsförsäljning
              </Link>
              <span className="mx-1.5">›</span>
              <span className="text-foreground">{region.label}</span>
            </>
          ) : (
            <span className="text-foreground">Gårdsförsäljning</span>
          )}
        </nav>

        {/* Hero */}
        <header className="max-w-2xl mb-10">
          <h1 className="font-display text-3xl sm:text-4xl text-foreground mb-4">
            {h1}
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            {isHub ? (
              <>
                Här samlar vi svenska producenter som erbjuder gårdsförsäljning
                av alkohol – vin, öl, cider och sprit, direkt på plats i samband
                med besök eller provning, enligt gällande regler.
              </>
            ) : (
              <>
                Producenter i {region!.label} med gårdsförsäljning av alkohol.
                Besök vingårdar, bryggerier och destillerier – provning och
                försäljning sker i samband med besök.
              </>
            )}
          </p>
        </header>

        {/* Dynamic region chips (hub only, gated ≥2) */}
        {isHub && showRegionChips && (
          <section className="mb-10">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">
              Utforska per region
            </h2>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => handleRegionSelect(null)}
                className={cn(
                  "inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors",
                  !activeRegionSlug
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground hover:bg-accent"
                )}
              >
                Alla
              </button>
              {dynamicRegions.map((r) => (
                <button
                  key={r.slug}
                  onClick={() => handleRegionSelect(r.slug)}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors",
                    activeRegionSlug === r.slug
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground hover:bg-accent"
                  )}
                >
                  {r.name}
                  <span className={cn(
                    "text-[10px]",
                    activeRegionSlug === r.slug
                      ? "text-primary-foreground/70"
                      : "text-muted-foreground"
                  )}>
                    ({r.count})
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Back to hub (region sub-page only) */}
        {!isHub && (
          <div className="mb-6">
            <Link
              to="/gardforsaljning"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
            >
              <ArrowLeft className="h-3 w-3" />
              Alla regioner
            </Link>
          </div>
        )}

        {/* Producer list */}
        <section>
          <h2 className="sr-only">Producenter med gårdsförsäljning</h2>
          {isLoading ? (
            <div className="space-y-4 max-w-2xl">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          ) : filteredProducers.length > 0 ? (
            <ul className="space-y-3 max-w-2xl">
              {filteredProducers.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/producer/${p.slug}`}
                    className="block rounded-lg border border-border/30 bg-card p-4 hover:border-border/60 hover:bg-accent/30 transition-colors"
                  >
                    <h3 className="font-display text-[16px] font-semibold text-foreground truncate">
                      {p.name}
                    </h3>
                    {p.short_description && (
                      <p className="text-sm text-foreground/85 mt-1 line-clamp-2">
                        {p.short_description}
                      </p>
                    )}
                    {/* Sale signals */}
                    <div className="flex gap-2 mt-2">
                      {p.has_farm_shop && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground/85">
                          <ShoppingBasket aria-hidden className="h-3.5 w-3.5 shrink-0" /> Handla
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground/85">
                        <Wine aria-hidden className="h-3.5 w-3.5 shrink-0" /> Gårdsförsäljning
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            /* ── Empty state ── */
            <div className="max-w-md py-12 text-center mx-auto">
              <h3 className="font-display text-xl text-foreground mb-2">
                Snart fler gårdar
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Vi bygger upp listan. Tipsa oss gärna om en producent som
                erbjuder gårdsförsäljning.
              </p>
              <a
                href="mailto:hej@farmly.se?subject=Tips%20om%20g%C3%A5rdsf%C3%B6rs%C3%A4ljning"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Mail className="h-4 w-4" />
                Tipsa oss
              </a>
            </div>
          )}
        </section>

        {/* FAQ (hub only) */}
        {isHub && (
          <section className="mt-16 max-w-2xl">
            <h2 className="font-display text-xl text-foreground mb-6">
              Vanliga frågor om gårdsförsäljning
            </h2>
            <dl className="space-y-6">
              {GARDFORSALJNING_FAQ.map((faq) => (
                <div key={faq.question}>
                  <dt className="text-sm font-medium text-foreground mb-1">
                    {faq.question}
                  </dt>
                  <dd className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* Legal note */}
        <p className="mt-12 text-[11px] text-muted-foreground/60 max-w-xl">
          Gårdsförsäljning sker i samband med besök, provning eller visning
          enligt gällande regler. Farmly tillhandahåller information och ansvarar
          inte för enskilda producenters tillstånd eller försäljning.
        </p>
      </main>

      <SiteFooter />
    </div>
  );
};

/** Simple slug helper: "Dalarna" → "dalarna", "Skåne" → "skane" */
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default GardforsaljningPage;
